import { useEffect, useRef, useState } from 'react';

import { rawToUnit, unitToRaw } from '../../sections/execution/execution-units';
import { BacktestingInput } from '../backtesting-input';

interface UnitValueInputProps {
  // Raw fraction string held in form state (e.g. "0.00025").
  rawValue: string;
  // Called with the new raw fraction string to write back to form state.
  onRawChange: (raw: string) => void;
  // display = raw * scale (bps -> 10000, % -> 100, absolute -> 1).
  scale: number;
  // Unit shown as a trailing adornment (e.g. "bps", "%", "USD").
  suffix: string;
  // Decimal places for the displayed value; float noise is stripped regardless.
  decimals?: number;
  placeholder?: string;
  errors?: string;
  size?: 'sm' | 'md' | 'lg' | 'fluid';
}

// A numeric input that shows a unit-native value (bps / % / USD) while keeping
// form state as the raw fraction. Conversion happens only here, at the input
// boundary, so schema/validation/API mapping are all unchanged. A local display
// string lets the user type freely (including "2." mid-entry) without the raw
// round-trip fighting the caret; it reformats to clean dp on blur.
export const UnitValueInput = ({
  rawValue,
  onRawChange,
  scale,
  suffix,
  decimals = 6,
  placeholder,
  errors,
  size = 'fluid',
}: UnitValueInputProps) => {
  const [display, setDisplay] = useState(() => rawToUnit(rawValue, scale, decimals));
  const isFocused = useRef(false);

  // Re-sync from raw when it changes externally (e.g. loading a saved config),
  // but never while the user is mid-edit.
  useEffect(() => {
    if (!isFocused.current) {
      setDisplay(rawToUnit(rawValue, scale, decimals));
    }
  }, [rawValue, scale, decimals]);

  return (
    <BacktestingInput
      size={size}
      inputMode="decimal"
      placeholder={placeholder}
      errors={errors}
      suffix={suffix}
      value={display}
      onFocus={() => {
        isFocused.current = true;
      }}
      onChange={(e) => {
        const next = e.target.value;
        setDisplay(next);
        onRawChange(unitToRaw(next, scale));
      }}
      onBlur={() => {
        isFocused.current = false;
        setDisplay(rawToUnit(rawValue, scale, decimals));
      }}
    />
  );
};
